import { useState, type ReactNode } from 'react'
import { Icon } from './Icon'
import { DiffView } from './DiffView'
import type { ConflictFile, ConflictReport } from '../api'
import { useDialog } from '../useDialog'

/**
 * Two writers wrote the same thing, and the second one gets THIS instead of a
 * silent overwrite or a dead end.
 *
 * It answers two refusals, because they are the same refusal at two heights.
 * A PAGE conflict is somebody else saving a page inside the product while it
 * was open. A COMMIT conflict is somebody pushing to the customer's own git
 * repository — from a laptop, from CI, from a tool nobody here controls —
 * while a draft was open. Both refuse to overwrite; both are useless without a
 * way to see what the other person wrote.
 *
 * One component rather than two, deliberately. The vocabulary is identical —
 * theirs, mine, keep, take — and two components drift until "take theirs"
 * means something slightly different on one screen than on the other.
 *
 * Rules the whole thing is built on:
 *
 * - Nothing is overwritten by being shown. The refusal already happened and
 *   nothing was written; pressing a button here is what writes, and every
 *   button says what it costs.
 * - Files nobody else touched never appear. They replay untouched, so putting
 *   forty of them on screen would bury the four that need a person — and a
 *   conflict screen that has to be scrolled past is one that gets clicked
 *   through.
 * - There is no automatic merge. Their paragraph and yours are both real and
 *   no machine should pick between them.
 */

interface PageProps {
  kind?: 'page'
  path: string
  mine: string
  theirs: string
  onKeepMine: () => void
  onTakeTheirs: () => void
  onClose: () => void
}

interface CommitProps {
  kind: 'commit'
  report: ConflictReport
  /** A commit is in flight; the decision must not be sent twice. */
  busy: boolean
  onResolve: (choices: Record<string, 'mine' | 'theirs'>) => void
  onClose: () => void
}

export function ConflictView(props: PageProps | CommitProps) {
  return props.kind === 'commit' ? <CommitConflict {...props} /> : <PageConflict {...props} />
}

/** The shell both refusals share: same drawer, same way out. */
function Shell({
  title,
  sub,
  onClose,
  children,
  actions,
}: {
  title: string
  sub: string
  onClose: () => void
  children: ReactNode
  actions: ReactNode
}) {
  // The shell both refusals share, so this holds the keyboard for both.
  const panel = useDialog<HTMLElement>(true, onClose)
  return (
    <div className="scrim" onMouseDown={onClose}>
      <aside
        className="drawer drawer--wide"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drawer__head">
          <div className="drawer__ident">
            <h2 className="drawer__title">{title}</h2>
            <p className="drawer__sub drawer__sub--wrap mono">{sub}</p>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>
        <div className="drawer__body">{children}</div>
        <footer className="drawer__foot">
          <div className="drawer__actions">{actions}</div>
        </footer>
      </aside>
    </div>
  )
}

/**
 * One page, saved twice.
 *
 * Both versions side by side, as text, because the person's next act is
 * editing words rather than admiring layout. "Take theirs" discards YOUR
 * unsaved words; "keep mine" replaces THEIRS on this draft. Both buttons say
 * so, on the click that does it.
 */
function PageConflict({ path, mine, theirs, onKeepMine, onTakeTheirs, onClose }: PageProps) {
  return (
    <Shell
      title="Somebody else saved this page"
      sub={path}
      onClose={onClose}
      actions={
        <>
          <button className="btn btn--outline" onClick={onTakeTheirs}>
            Take theirs — my unsaved words are discarded
          </button>
          <button className="btn btn--outline" onClick={onKeepMine}>
            Keep mine — theirs are replaced on this draft
          </button>
          <button className="btn btn--ghost" onClick={onClose}>
            Decide later
          </button>
        </>
      }
    >
      <div className="drawer__block">
        <p className="drawer__hint">
          While you were editing, this page changed underneath you. Nothing is lost: your words are
          below on the right, theirs on the left. Read both, pick one to start from, and put
          anything from the other back in by hand — or close this and copy pieces across yourself.
        </p>
        <div className="compare compare--conflict">
          <div className="compare__pane">
            <div className="compare__title">Theirs — now on the draft</div>
            <pre className="conflict__text">{theirs}</pre>
          </div>
          <div className="compare__pane">
            <div className="compare__title">Yours — unsaved</div>
            <pre className="conflict__text">{mine}</pre>
          </div>
        </div>
      </div>
    </Shell>
  )
}

/**
 * The branch moved while a draft was open.
 *
 * Every file gets one choice and both versions of the words behind it, and
 * nothing is applied until the one button at the bottom. That ordering is the
 * point: a screen of per-file buttons that each write immediately is a screen
 * where somebody is three clicks into a decision they have not finished
 * making.
 */
function CommitConflict({ report, busy, onResolve, onClose }: CommitProps) {
  const [choices, setChoices] = useState<Record<string, 'mine' | 'theirs'>>({})
  const undecided = report.conflicts.filter((file) => choices[file.path] === undefined)
  const untouched = report.replays.length + report.identical.length

  return (
    <Shell
      title="Somebody else committed to this draft"
      sub={`${report.branch} · now at ${short(report.actual)}, you started from ${short(report.expected)}`}
      onClose={onClose}
      actions={
        <>
          {report.conflicts.length > 0 && (
            <button
              className="btn btn--primary"
              disabled={busy || undecided.length > 0}
              data-conflict-commit="1"
              onClick={() => onResolve(choices)}
            >
              {undecided.length > 0
                ? `${undecided.length} still to decide`
                : 'Commit with these choices'}
            </button>
          )}
          <button className="btn btn--ghost" onClick={onClose}>
            Decide later
          </button>
        </>
      }
    >
      <div className="drawer__block">
        <p className="drawer__hint">
          Your content lives in a git repository, and somebody pushed to it while you were writing.
          Nothing was overwritten — their commit is on the branch, your work is still staged here,
          and neither has touched the other.
        </p>
      </div>

      {report.conflicts.length === 0 ? (
        /* Nothing of theirs is in the way. Say so, and offer the one move. */
        <div className="drawer__block">
          <div className="empty">
            <span className="empty__icon">
              <Icon name="branch" size={20} />
            </span>
            <span className="empty__title">Nothing of yours clashes</span>
            <span className="empty__text">
              Their commit does not touch{' '}
              {untouched === 1 ? 'the file' : `any of the ${untouched} files`} you staged. Commit
              again and yours land on top of theirs.
            </span>
            <button
              className="btn btn--primary"
              disabled={busy}
              data-conflict-commit="1"
              onClick={() => onResolve({})}
            >
              Commit on top of theirs
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="drawer__block">
            <p className="drawer__hint">
              <strong>
                {report.conflicts.length} file{report.conflicts.length === 1 ? '' : 's'} need
                {report.conflicts.length === 1 ? 's' : ''} a decision.
              </strong>{' '}
              {untouched > 0 && (
                <>
                  The {untouched === 1 ? 'other file' : `other ${untouched} files`} you staged{' '}
                  {untouched === 1 ? 'replays' : 'replay'} untouched:{' '}
                  <span className="mono">
                    {[...report.replays, ...report.identical].join(', ')}
                  </span>
                  .
                </>
              )}
            </p>
          </div>
          {report.conflicts.map((file) => (
            <Decision
              key={file.path}
              file={file}
              choice={choices[file.path] ?? null}
              onChoose={(pick) => setChoices((held) => ({ ...held, [file.path]: pick }))}
            />
          ))}
        </>
      )}
    </Shell>
  )
}

/** One file, one choice, and the words on both sides of it. */
function Decision({
  file,
  choice,
  onChoose,
}: {
  file: ConflictFile
  choice: 'mine' | 'theirs' | null
  onChoose: (pick: 'mine' | 'theirs') => void
}) {
  return (
    <section className="drawer__block conflict" data-conflict={file.path}>
      <div className="conflict__head">
        <span className="mono truncate">{file.movedFrom ?? file.path}</span>
        <span className="badge badge--warn">{LABEL[file.movedFrom ? 'moved' : file.kind]}</span>
      </div>
      <p className="drawer__hint">{why(file)}</p>
      <div
        className="segmented conflict__choice"
        role="group"
        aria-label={`What to do with ${file.path}`}
      >
        <button
          className={`segmented__btn${choice === 'mine' ? ' is-active' : ''}`}
          aria-pressed={choice === 'mine'}
          data-choice="mine"
          onClick={() => onChoose('mine')}
        >
          Keep mine
        </button>
        <button
          className={`segmented__btn${choice === 'theirs' ? ' is-active' : ''}`}
          aria-pressed={choice === 'theirs'}
          data-choice="theirs"
          onClick={() => onChoose('theirs')}
        >
          Take theirs
        </button>
      </div>
      {/* What the choice costs, at the moment it is made rather than in a
          paragraph above the fold. Before a choice exists it says so, because
          "nothing selected" is a state a person has to be able to notice. */}
      <p className={`conflict__cost${choice ? '' : ' conflict__cost--open'}`}>
        {choice
          ? COST[choice][file.movedFrom ? 'moved' : file.kind]
          : 'Pick one — nothing is written until you commit.'}
      </p>
      <p className="conflict__legend">
        <span className="conflict__legend-del">− on the branch</span>
        <span className="conflict__legend-add">+ staged here</span>
      </p>
      <div className="drawer__diff">
        <DiffView diff={file.diff} />
      </div>
    </section>
  )
}

const short = (sha: string) => sha.slice(0, 8)

/** A move is its own case: the same kind, at a different address. */
type Case = ConflictFile['kind'] | 'moved'

const LABEL: Record<Case, string> = {
  changed: 'Both edited',
  deleted: 'They deleted',
  added: 'Both added',
  moved: 'Moved away',
  // Never rendered — the server keeps these out of `conflicts` — but a map
  // with holes in it is a map that throws the day one gets through.
  clean: 'Untouched',
  identical: 'Same words',
}

const COST: Record<'mine' | 'theirs', Record<Case, string>> = {
  mine: {
    changed: 'Your words replace theirs when you commit.',
    deleted: 'The page comes back, with your words in it.',
    added: 'Your version replaces theirs when you commit.',
    moved: 'The move goes ahead, and their version at the old address goes with it.',
    clean: 'Yours lands, as staged.',
    identical: 'Yours lands, as staged.',
  },
  theirs: {
    changed: 'Their version stands. Your staged edit is dropped.',
    deleted: 'The page stays deleted. Your staged edit is dropped.',
    added: 'Their page stands. Your staged one is dropped.',
    moved: 'The move is dropped, and their page stays where it is.',
    clean: 'Nothing changes.',
    identical: 'Nothing changes.',
  },
}

function why(file: ConflictFile): string {
  if (file.movedFrom) {
    return `You moved this page to ${file.path}. Somebody edited it where it was, so committing the move as it stands would take their words with it.`
  }
  if (file.kind === 'deleted') return 'They deleted this page. Your version is still staged here.'
  if (file.kind === 'added') {
    return 'This page did not exist when you started. You both created one, with different words.'
  }
  return 'They edited this page after you started, so there are two versions of it.'
}
